/**
 * EditCohortEventDateTimeModal Component
 * Modal for setting or editing a cohort event's date and time (MED-92 / E8-14)
 */

import { useState, useEffect } from 'react';
import { Modal, Stack, Group, Button, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  cohortEventsApi,
  type CohortEventWithDetails,
} from '../../../../services/api/cohortEvents';
import { DateTimeField } from '../../../../components/common';
import { endAfterStartError, endForNewStart, eventLengthMs } from './cohortEventDateTime';
import { endFieldDefaults } from '../../../../utils/sessionEndTime';

interface EditCohortEventDateTimeModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  event: CohortEventWithDetails | null;
  onSaved?: () => void;
}

export function EditCohortEventDateTimeModal({
  opened,
  onClose,
  cohortId,
  event,
  onSaved,
}: EditCohortEventDateTimeModalProps) {
  const [scheduledStart, setScheduledStart] = useState<Date | null>(null);
  const [scheduledEnd, setScheduledEnd] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Initialize from event when modal opens
  useEffect(() => {
    if (!opened || !event) return;
    setScheduledStart(event.scheduled_start ? new Date(event.scheduled_start) : null);
    setScheduledEnd(event.scheduled_end ? new Date(event.scheduled_end) : null);
  }, [opened, event]);

  const lengthMs = event ? eventLengthMs(event.scheduled_start, event.scheduled_end) : null;
  const endError = endAfterStartError(scheduledStart, scheduledEnd);

  // MED-1349 QA round 4: the end always follows the start and keeps the
  // current length; with no end it gets the event's saved length. The CM
  // can still change the end by hand.
  const handleStartChange = (value: Date | null) => {
    setScheduledEnd(endForNewStart(scheduledStart, scheduledEnd, value, lengthMs));
    setScheduledStart(value);
  };

  // MED-1360: an empty End field opens on the start's month and shows a time.
  // A date-only pick gives the start plus the saved length, or the shared
  // fallback length for an event with no end yet. A typed end is kept as
  // typed; endError reports one at or before the start.
  const lengthMinutes = lengthMs !== null ? lengthMs / 60_000 : null;
  const endDefaults = endFieldDefaults(scheduledStart, lengthMinutes);

  const handleSubmit = async () => {
    if (!event || !scheduledStart) {
      notifications.show({
        title: 'Validation Error',
        message: 'Start date/time is required',
        color: 'red',
      });
      return;
    }

    if (endError) return;

    setSubmitting(true);
    try {
      await cohortEventsApi.updateDateTime(cohortId, event.id, {
        scheduled_start: scheduledStart.toISOString(),
        scheduled_end: scheduledEnd?.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      notifications.show({
        title: 'Date/Time Updated',
        message: `${event.event_name} date/time has been updated`,
        color: 'green',
      });

      onSaved?.();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update date/time',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!event) return null;

  const isEdit = !!event.scheduled_start;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} size="lg">
          {isEdit ? 'Edit Event Date/Time' : 'Set Event Date/Time'}
        </Text>
      }
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {event.event_name}
        </Text>

        <DateTimeField
          label="Start Date & Time"
          placeholder="Select start date and time"
          value={scheduledStart}
          onChange={handleStartChange}
          defaultTimeOnDatePick="09:00"
          required
        />

        <DateTimeField
          label="End Date & Time"
          placeholder="Select end date and time"
          value={scheduledEnd}
          onChange={setScheduledEnd}
          minDate={scheduledStart || undefined}
          defaultDate={endDefaults.defaultDate}
          defaultTimeOnDatePick={endDefaults.defaultTimeOnDatePick}
          valueOnFirstDatePick={endDefaults.endOnDatePick}
          error={endError ?? undefined}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitting}
            disabled={!scheduledStart || endError !== null}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditCohortEventDateTimeModal;
